<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import { maskDateBR } from "@/common/utils/mask";

import {
  baseInput,
  baseTextarea,
  baseSelect,
  baseDate,
} from "@/common/components";

import { SamplePayloadType } from "@/common/types/sample";

import { ItemStatusEnum } from "@/common/types/enum";

const { t, tm } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: SamplePayloadType | null;
    title?: string;
    width?: string;
    methods?: { option: string; value: string }[];
    showMethod?: boolean;
    showStatus?: boolean;
    isPatch?: boolean;
    inUse?: boolean;
  }>(),
  {
    width: "100%",
    showMethod: false,
    showStatus: false,
    isPatch: false,
    inUse: false,
  },
);

const defaultData = {
  code: "",
  description: null,
  name: "",
  status: null,
  method_id: null,
  stability: null,
  validity: null,
};

const payload = ref<SamplePayloadType>({ ...defaultData });

const statusUnusedEnum = computed(
  () => tm("enums.item_status") as Record<string, string>,
);

const statusInUseEnum = computed(() => ({
  [ItemStatusEnum.IN_USE]: t("enums.item_status.IN_USE"),
  [ItemStatusEnum.DEFECT]: t("enums.item_status.DEFECT"),
  [ItemStatusEnum.BROKEN]: t("enums.item_status.BROKEN"),
}));

const statusOptions = computed(() => {
  if (props.inUse) {
    return Object.entries(statusInUseEnum.value).map(([key, value]) => ({
      option: value,
      value: key,
    }));
  }

  return Object.entries(statusUnusedEnum.value).map(([key, value]) => ({
    option: value,
    value: key,
  }));
});

function update(key: keyof SamplePayloadType) {
  if (props.isPatch) {
    emit("update-data", { [key]: payload.value[key] });
    return;
  }

  emit("update-data", payload.value);
}

function maskCode(value: string) {
  return value?.replaceAll(" ", "-")?.toUpperCase();
}

watchEffect(() => {
  if (props.data) {
    payload.value = {
      code: props.data.code,
      description: props.data.description,
      name: props.data.name,
      status: props.data.status,
      method_id: props.data.method_id,
      stability: props.data.stability,
      validity: props.data.validity,
    };
  }
});
</script>

<template>
  <section class="form-content" :style="{ width: width }">
    <form>
      <base-input
        v-model="payload.code"
        :value="payload.code"
        :placeholder="t('common.placeholders.code')"
        :mask="maskCode"
        @input="update('code')"
      >
        <template #label>
          {{ t("common.labels.code") }}
        </template>
      </base-input>

      <base-input
        v-model="payload.name"
        :value="payload.name"
        :placeholder="t('common.placeholders.name')"
        @input="update('name')"
      >
        <template #label>
          {{ t("common.labels.name") }}
        </template>
      </base-input>

      <template v-if="showStatus">
        <base-select
          v-model="payload.status"
          :value="payload.status"
          :options="statusOptions"
          :is-empty="false"
          @change="update('status')"
        >
          <template #label>
            {{ t("common.labels.status") }}
          </template>
        </base-select>
      </template>

      <base-date
        v-model="payload.validity"
        :value="payload.validity"
        :required="true"
        value-mask="YYYY-MM-DD"
        placeholder="--/--/----"
        @update:model-value="update('validity')"
      >
        <template #label>
          {{ t("common.labels.validity") }}
        </template>
      </base-date>

      <template v-if="showMethod && methods">
        <base-select
          v-model="payload.method_id"
          :value="payload.method_id"
          :options="methods"
          :is-empty="true"
          @change="update('method_id')"
        >
          <template #label>
            {{ t("common.labels.method") }}
          </template>
        </base-select>
      </template>

      <base-textarea
        v-model="payload.description"
        :value="payload.description"
        :placeholder="t('common.placeholders.describe')"
        @input="update('description')"
      >
        <template #label>
          {{ t("common.labels.description") }}
        </template>
      </base-textarea>
    </form>
  </section>
</template>

<style lang="scss" scoped>
.form-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
